import { GitBranchIcon } from '@primer/octicons-react'
import * as Accordion from '@radix-ui/react-accordion'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronDownIcon, ChevronUpIcon, Cross2Icon, ExternalLinkIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import * as Tabs from '@radix-ui/react-tabs'
import { match } from 'ts-pattern'
import { mappedColors, typography } from '../../main.css.ts'
import { Routes } from '../../routes.ts'
import { Changeset, Finding, Fix, Patch, RepositoryType } from '../../utils/api-client/api-schemas.ts'
import { Theme } from '../../utils/higher-order-components/with-theme.tsx'
import { CopyButton } from '../buttons/copy-button.tsx'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { InProgressPulse } from '../in-progress-pulse.tsx'
import { Markdown } from '../markdown.tsx'
import { FixStrategyTag } from '../tags/fix-strategy-tag.tsx'
import { Tooltip } from '../tooltip.tsx'
import * as styles from './finding-fix-drawer.css.ts'

export interface FindingFixDrawerProps {
  analysisId: string
  findingId: string
  fix?: Fix
  changesets?: Changeset[]
  patch?: Patch
  findingTriageResult?: NonNullable<NonNullable<Finding['_embedded']>['triage']>['result']
  repositoryType?: RepositoryType
  isSendPatchLoading: boolean
  handleSendPatch: () => void
  onClose: () => void
  theme: Theme
}

export const FindingFixDrawer: React.FC<FindingFixDrawerProps> = ({
  analysisId,
  findingId,
  fix,
  changesets,
  patch,
  findingTriageResult,
  repositoryType,
  isSendPatchLoading,
  handleSendPatch,
  onClose,
  theme,
}) => {
  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Content className={styles.content} aria-describedby={undefined}>
        <Dialog.Close asChild className={styles.closeButton}>
          <button aria-label="Close" onClick={onClose}>
            <Cross2Icon className={styles.closeButtonIcon} />
          </button>
        </Dialog.Close>
        <FixPreviewHeader
          fix={fix}
          patch={patch}
          findingId={findingId}
          isSendPatchLoading={isSendPatchLoading}
          handleSendPatch={handleSendPatch}
          repositoryType={repositoryType}
        />
        <Tabs.Root defaultValue="overview" className={styles.tabContainer}>
          <div style={{ position: 'relative' }}>
            <Tabs.List className={styles.tabList}>
              <Tabs.Trigger className={styles.tab} value="overview">
                OVERVIEW
              </Tabs.Trigger>
              <Tabs.Trigger className={styles.tab} value="files-changed">
                FILES CHANGED <span className={styles.changesetCount}>{changesets?.length}</span>
              </Tabs.Trigger>
              <Tabs.Trigger className={styles.tab} value="details">
                DETAILS
              </Tabs.Trigger>
            </Tabs.List>
            {findingTriageResult === 'triaged' && (
              <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                <GhostButton to={Routes.AnalysisDetailsPageFindingTriageDrawer.createPath({ analysisId, findingId })}>
                  <EyeOpenIcon style={{ marginRight: '4px', color: mappedColors.neutral.white, width: '16px', height: '16px' }} />
                  <span className={typography.small}>Related analysis</span>
                </GhostButton>
              </div>
            )}
          </div>
          <Tabs.Content className={styles.tabContent} value="overview">
            {fix?.description ? (
              <Markdown language={getCodemodLanguageByCodemodId(fix?.codemod) ?? 'text'} markdown={fix.description} theme={theme} />
            ) : (
              <p className={typography.paragraph}>No description available for this fix.</p>
            )}
          </Tabs.Content>
          <Tabs.Content className={styles.tabContent} value="files-changed">
            {changesets && <ChangesetBlocksPure changesets={changesets} theme={theme} />}
          </Tabs.Content>
          <Tabs.Content className={styles.tabContent} value="details">
            {fix?.confidence && fix?.quality ? (
              <>
                <h1 className={typography.h6} style={{ marginBottom: '16px' }}>
                  <span className={styles.detailsLabel}>Confidence</span> <br />
                  {confidenceToText(fix?.confidence)}
                </h1>
                <h2 className={typography.leadBold}>
                  <span className={styles.detailsLabel}>Safety</span> {fix?.quality?.safety.score}
                </h2>
                <p className={typography.paragraph}>{fix?.quality?.safety.description}</p>
                <h2 className={typography.leadBold}>
                  <span className={styles.detailsLabel}>Effectiveness</span> {fix?.quality?.effectiveness.score}
                </h2>
                <p className={typography.paragraph}>{fix?.quality?.effectiveness.description}</p>
                <h2 className={typography.leadBold}>
                  <span className={styles.detailsLabel}>Cleanliness</span> {fix?.quality?.cleanliness.score}
                </h2>
                <p className={typography.paragraph}>{fix?.quality?.cleanliness.description}</p>
              </>
            ) : (
              <p className={typography.paragraph}>No details available for this fix.</p>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  )
}

interface FixPreviewHeaderProps {
  fix?: Fix
  patch?: Patch
  repositoryType?: RepositoryType
  findingId: string
  isSendPatchLoading: boolean
  handleSendPatch: () => void
}
const FixPreviewHeader: React.FC<FixPreviewHeaderProps> = ({ fix, patch, findingId, isSendPatchLoading, handleSendPatch, repositoryType }) => {
  return (
    <section className={styles.header} aria-label="Fix preview header">
      <Dialog.Title className={styles.title}>{fix?.summary}</Dialog.Title>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '16px' }}>
        {fix && (
          <div className={styles.fixIdGroup}>
            <label htmlFor="fix-id" className={styles.label}>
              Fix
            </label>{' '}
            <output id="fix-id" className={styles.fixIdOutput}>
              {fix.id}
            </output>
            <CopyButton text={fix.id} label="Copy Fix ID to clipboard" className={styles.copyButton} />
          </div>
        )}
        {fix?.confidence && (
          <div className={styles.fixTagWithTooltip}>
            <label htmlFor="fix-confidence" className={styles.label}>
              Confidence
            </label>{' '}
            <output id="fix-confidence" className={styles.confidenceOutput}>
              {confidenceToText(fix.confidence)}
            </output>
            <FixConfidenceTooltip strategy={fix.strategy} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '8px' }}>
        <div className={styles.fixIdGroup}>
          <label htmlFor="finding-id" className={styles.label}>
            Finding
          </label>{' '}
          <output id="finding-id" className={styles.findingIdOutput}>
            {findingId}
          </output>
          <CopyButton text={findingId} label="Copy Finding ID to clipboard" className={styles.copyButton} />
        </div>
        {fix?.strategy && (
          <div className={styles.fixTagWithTooltip}>
            <FixStrategyTag strategy={fix.strategy} />
          </div>
        )}
      </div>
      <div style={{ marginTop: '16px' }}>
        {match(patch?.state)
          .with('in_progress', () => (
            <div className={styles.generatingFixContainer}>
              <span role="status" aria-label="Generating fix" className={styles.generatingFix}>
                Generating fix
              </span>
              <InProgressPulse className={styles.generatingFixIcon} />
            </div>
          ))
          .with('completed_success', () => {
            return match(patch?.type)
              .with('git', () => {
                if (patch?.type !== 'git') return null

                return (
                  <div className={styles.successFixContainerMeta}>
                    <div className={styles.successFixContainer}>
                      <span role="status" aria-label={getSuccessPatchStateText(patch?.type)} className={styles.successFix}>
                        {getSuccessPatchStateText(patch?.type)}
                      </span>
                      <GitBranchIcon className={styles.successFixIcon} />
                    </div>
                    {patch.branch_name && (
                      <div className={styles.successFixBranchNameContainer}>
                        <span className={styles.successFixBranchName}>{patch.branch_name}</span>
                        <CopyButton text={patch.branch_name} label="Copy branch name to clipboard" className={styles.copyButton} />
                      </div>
                    )}
                  </div>
                )
              })

              .otherwise(() => {
                if (patch?.type === 'github' || patch?.type === 'gitlab' || patch?.type === 'bitbucket' || patch?.type === 'azure')
                  return (
                    <div className={styles.successFixContainerMeta}>
                      <div className={styles.successFixContainer}>
                        <span role="status" aria-label={getSuccessPatchStateText(patch?.type)} className={styles.successFix}>
                          {getSuccessPatchStateText(patch?.type)}
                        </span>
                        <GitBranchIcon className={styles.successFixIcon} />
                      </div>
                      {patch.pr_number && (
                        <a href={patch.html_url!} target="_blank" className={styles.successFixLink}>
                          #{patch.pr_number}
                          <ExternalLinkIcon className={styles.successFixLinkIcon} />
                        </a>
                      )}
                    </div>
                  )
              })
          })
          .with('completed_failed', () => (
            <div className={styles.generatingFixContainer}>
              <DefaultButton
                buttonType="secondary"
                state={isSendPatchLoading ? 'loading' : 'default'}
                loadingLabel={'Fix request in progress...'}
                onClick={handleSendPatch}
                size="small"
              >
                Try again
              </DefaultButton>
              <span role="status" aria-label={getFailPatchStateText(patch?.type)} className={styles.failedFix}>
                {getFailPatchStateText(patch?.type)}
                <Cross2Icon className={styles.failedFixIcon} />
              </span>
            </div>
          ))
          .with(undefined, () => (
            <DefaultButton
              state={isSendPatchLoading ? 'loading' : 'default'}
              ariaLabel={getActionButtonText(repositoryType)}
              loadingLabel={'Fix request in progress...'}
              onClick={handleSendPatch}
              size="small"
            >
              {getActionButtonText(repositoryType)}
            </DefaultButton>
          ))
          .exhaustive()}
      </div>
    </section>
  )
}

const FixConfidenceTooltip: React.FC<{ strategy: Fix['strategy'] }> = ({ strategy }) => {
  return (
    <Tooltip label="See details about fix confidence and strategy" width={250} side="bottom" sideOffset={10} contentClassName={styles.tooltipContent}>
      <>
        <p className={typography.extraSmallBold} style={{ textTransform: 'uppercase', color: mappedColors.neutral.neutral30, marginBottom: '16px' }}>
          Beta Feature
        </p>
        <p style={{ marginBottom: '0' }}>
          <b>Fix confidence</b> is a rating derived from an internal benchmark and includes High, Medium, and Low confidence fixes. It is comprised of
          three weighted scores reflecting the safety, effectiveness and cleanliness of Pixee’s code changes within a fix.{' '}
          <b>See Details tab for more information.</b>
        </p>
        <br />
        <p style={{ marginBottom: '0' }}>
          {match(strategy)
            .with('deterministic', () => (
              <>
                <b>Deterministic fixes</b> are developed by Pixee using traditional algorithmic means. Generative AI is NOT used to write source code.
              </>
            ))
            .with('ai', () => (
              <>
                <b>AI fixes</b> are architected by Pixee to leverage specialized AI directed at the explicit finding type and instance.
              </>
            ))
            .with('hybrid', () => (
              <>
                <b>Hybrid fixes</b> are architected by Pixee to incorporate both deterministic and directed AI techniques.
              </>
            ))
            .with('provisional_ai', () => (
              <>
                <b>Provisional fixes</b> are those which rely on more abstract and generic techniques to create the fix. As a result, these fixes may
                be more unpredictable and warrant additional review.
              </>
            ))
            .exhaustive()}
        </p>
      </>
    </Tooltip>
  )
}

export const getActionButtonText = (repositoryType?: RepositoryType): string => {
  return match(repositoryType)
    .with('gitlab', () => 'Create merge request')
    .with('github', () => 'Create pull request')
    .with('azure', () => 'Create pull request')
    .with('bitbucket', () => 'Create pull request')
    .with('git', () => 'Push to branch')
    .with(undefined, () => 'Push to branch')
    .exhaustive()
}

export const getSuccessPatchStateText = (repositoryType?: RepositoryType): string => {
  return match(repositoryType)
    .with('gitlab', () => 'MR created')
    .with('github', () => 'PR created')
    .with('azure', () => 'PR created')
    .with('bitbucket', () => 'PR created')
    .with('git', () => 'Pushed to branch')
    .with(undefined, () => 'Pushed to branch')
    .exhaustive()
}

export const getFailPatchStateText = (repositoryType?: RepositoryType): string => {
  return match(repositoryType)
    .with('gitlab', () => 'MR creation failed')
    .with('github', () => 'PR creation failed')
    .with('azure', () => 'PR creation failed')
    .with('bitbucket', () => 'PR creation failed')
    .with('git', () => 'Push to branch failed')
    .with(undefined, () => 'Push to branch failed')
    .exhaustive()
}

export const getCodemodLanguageByCodemodId = (codemodId: string | undefined): string | undefined => {
  if (!codemodId) return undefined
  const [_, language] = codemodId.split(/:([^/]+)\//)
  return language
}

const ChangesetBlocksPure = ({ changesets, theme }: { changesets: Changeset[]; theme: Theme }) => {
  return (
    <Accordion.Root
      type="multiple"
      defaultValue={changesets.map(changeset => changeset.path + changeset.preview.length)}
      className={styles.accordion}
    >
      {changesets.map(changeset => (
        <Accordion.Item
          key={changeset.path + changeset.preview.length}
          value={changeset.path + changeset.preview.length}
          className={styles.accordionItem}
        >
          <Accordion.Header className={styles.accordionHeader}>
            <Accordion.Trigger className={styles.accordionTrigger}>
              <ChevronDownIcon className={styles.chevronDown} />
              <ChevronUpIcon className={styles.chevronUp} />
              {changeset.path}
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content>
            <Markdown theme={theme} markdown={'```\n' + changeset.preview + '\n```'} />
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

const confidenceToText = (confidence: NonNullable<Fix['confidence']>): string =>
  match(confidence)
    .with('low', () => 'Low')
    .with('medium', () => 'Medium')
    .with('high', () => 'High')
    .exhaustive()
